import apiClient from './client';
import { AboutUsSettings, ContactSettings, SliderSettings } from '@/types';

export interface SettingsResponse {
  name?: string | null;
  email?: string | null;
  alamat?: string | null;
  telepon?: string | null;
  tentang_kami?: string | null;
  tentang_kami_label?: string | null;
  tentang_kami_title?: string | null;
  tentang_kami_cover?: string | null;
  about_us?: {
    label?: string | null;
    title?: string | null;
    description?: string | null;
    content?: string | null;
    cover_image?: string | null;
  } | null;
  slider?: {
    amount?: number;
    slide_count?: number;
    interval_value?: number;
    interval_unit?: 'seconds' | 'ms';
    news_ids?: number[];
    news?: SliderSettings['news'];
  } | null;
}

export interface UpdateSettingsResponse {
  message: string;
  data: ContactSettings;
}

export interface UpdateAboutUsSettingsResponse {
  message: string;
  data: {
    content?: string | null;
    cover_image?: string | null;
  };
}

export interface UploadAboutUsImageResponse {
  message: string;
  url: string;
}

export interface UpdateHomePageSettingsResponse {
  message: string;
  data: {
    amount?: number;
    slide_count?: number;
    interval_value?: number;
    interval_unit?: 'seconds' | 'ms';
    news_ids?: number[];
    news?: SliderSettings['news'];
  };
}

const FALLBACK_CONTACT = {
  email: 'info@smalabaik.com',
  phone: '+6281234567890',
};

export const settingsApi = {
  getContact: async (): Promise<ContactSettings> => {
    const response = await apiClient.get<SettingsResponse>('/api/settings.json');
    return {
      phone: response.data.telepon ?? FALLBACK_CONTACT.phone,
      email: response.data.email ?? FALLBACK_CONTACT.email,
      address: response.data.alamat ?? null,
    };
  },

  getAboutUs: async (): Promise<AboutUsSettings> => {
    const response = await apiClient.get<SettingsResponse>('/api/settings.json');
    const aboutUs = response.data.about_us;

    return {
      label: aboutUs?.label ?? response.data.tentang_kami_label ?? 'Tentang Kami',
      title: aboutUs?.title ?? response.data.tentang_kami_title ?? 'Membangun Kebaikan Bersama Masyarakat',
      description: aboutUs?.description ?? aboutUs?.content ?? response.data.tentang_kami ?? '',
      content: aboutUs?.description ?? aboutUs?.content ?? response.data.tentang_kami ?? '',
      coverImage: aboutUs?.cover_image ?? response.data.tentang_kami_cover ?? null,
    };
  },

  updateContact: async (data: ContactSettings): Promise<UpdateSettingsResponse> => {
    const response = await apiClient.put('/api/settings/contact.json', data);
    return response.data;
  },

  updateAboutUs: async (data: AboutUsSettings): Promise<UpdateAboutUsSettingsResponse> => {
    const response = await apiClient.put('/api/settings/about.json', {
      label: data.label,
      title: data.title,
      description: data.description ?? data.content,
      content: data.description ?? data.content,
      cover_image: data.coverImage,
    });
    return response.data;
  },

  uploadAboutUsImage: async (file: File): Promise<UploadAboutUsImageResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/api/settings/about/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getHomePage: async (): Promise<SliderSettings> => {
    const response = await apiClient.get<{ data?: SettingsResponse['slider'] }>('/api/settings/slider.json');
    const slider = response.data.data;
    return {
      amount: Number(slider?.amount ?? slider?.slide_count ?? 0),
      intervalValue: Number(slider?.interval_value ?? 5),
      intervalUnit: slider?.interval_unit === 'ms' ? 'ms' : 'seconds',
      newsIds: Array.isArray(slider?.news_ids) ? slider?.news_ids : [],
      news: Array.isArray(slider?.news) ? slider.news : [],
    };
  },

  updateHomePage: async (data: SliderSettings): Promise<UpdateHomePageSettingsResponse> => {
    const response = await apiClient.put('/api/settings/slider.json', {
      amount: data.amount,
      news_ids: data.newsIds,
      interval_value: data.intervalValue,
      interval_unit: data.intervalUnit,
    });
    return response.data;
  },
};
